import { isActiveFilterRow } from './isActiveFilterRow';
import { normalizeFilterRows } from './presets';
import { FilterColumnDef, FilterRow, MapScenarioFilterV1 } from './types';

/**
 * 「行が絞り込みに寄与するか」の判定を isActiveFilterRow に一本化して返す。
 * 件数バッジ（表示側）とシナリオ保存（通知側）が別々に同じ絞り込みを書くと、
 * バッジの件数と保存される内容が食い違いうるため、両方からここを呼ぶ。
 *
 * Why not 単純な .filter(): 先頭行は 'where' 固定・削除不可という不変条件
 * （types.ts の FilterRow.combinator コメント）があり、AdvancedFilterPopover は
 * row.combinator の値そのもの（配列の添字ではない）で Where ラベル・削除ボタンの
 * 出し分けを行う。単純 filter で先頭の Where 行だけを落とすと、残った行が
 * combinator: 'and'/'or' のまま浮いてしまい、この不変条件を破った FilterRow[] が
 * 外部（保存されたシナリオ）に漏れる。normalizePresetRowsForSave と同じ理由で
 * normalizeFilterRows を使い、OR グループを保ったまま無効行を落とし、
 * 生き残った行の先頭を where へ振り直す。
 */
export function filterActiveRows(rows: FilterRow[], columnsByKey: Map<string, FilterColumnDef>): FilterRow[] {
  return normalizeFilterRows(rows, (row) => {
    const column = columnsByKey.get(row.column);
    return column && isActiveFilterRow(row, column) ? row : null;
  });
}

/**
 * シナリオ保存用スナップショットを組み立てる純粋関数。
 * sort・displayMode・filter popover の開閉状態はここでは扱わない（保存対象外）。
 */
export function buildMapScenarioFilter(
  freeText: string,
  rows: FilterRow[],
  columnsByKey: Map<string, FilterColumnDef>,
): MapScenarioFilterV1 {
  return {
    kind: 'map',
    version: 1,
    freeText,
    rows: filterActiveRows(rows, columnsByKey),
  };
}

/**
 * 通知先へ渡す直前に使う deep copy。FilterRow はプリミティブ値のみを持つ純粋なデータ
 * （getValue 等の関数を持つのは FilterColumnDef 側で、ここには含まれない）ため、
 * JSON 往復で複製できる。受信側が返り値の配列・オブジェクトを書き換えても、
 * 内部 state（advancedFilters）の同一オブジェクト参照を汚さないために必要。
 */
export function cloneMapScenarioFilter(filter: MapScenarioFilterV1): MapScenarioFilterV1 {
  return JSON.parse(JSON.stringify(filter)) as MapScenarioFilterV1;
}
